import { baseApi } from "./base";
import { TAG_TYPE_CARD_PREFERENCES } from "./tags";

const apiWithTag = baseApi.enhanceEndpoints({
  addTagTypes: [TAG_TYPE_CARD_PREFERENCES],
});

/**
 * Company-specific UI label overrides
 * Stored in database per company
 *
 * API Documentation: See BACKEND_STRUCTURE_UI_LABELS.md
 *
 * Endpoints (Recommended - using company from auth context):
 * - GET /api/ui-labels
 * - POST /api/ui-labels
 * - PUT /api/ui-labels
 *
 * Alternative (explicit company path):
 * - GET /api/company/{company}/ui-labels
 * - POST /api/company/{company}/ui-labels
 * - PUT /api/company/{company}/ui-labels
 *
 * Note: This implementation uses the recommended route (/api/ui-labels)
 * which automatically uses the company from the authenticated user's context.
 * No company_id needs to be passed in the URL.
 */
export interface CompanyUILabels {
  id?: number;
  company_id?: number;
  labels: Record<string, string>; // e.g., { "common.location": "Site", "common.locationAddress": "Site Address" }
  created_at?: string;
  updated_at?: string;
}

export interface UpdateUILabelsRequest {
  labels: Record<string, string>;
}

/**
 * Error response format from backend
 */
export interface UILabelsErrorResponse {
  message: string;
  errors?: Record<string, string[]>;
}

export const uiLabelApi = apiWithTag.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /api/ui-labels
     *
     * Gets custom UI labels for the authenticated company.
     *
     * Response codes:
     * - 200: Success (returns CompanyUILabels)
     * - 404: Not found (no custom labels exist yet - frontend should use defaults)
     * - 403: Forbidden (user has no company associated)
     * - 401: Unauthorized (invalid/missing token)
     *
     * Note: 404 is handled as a normal case (no custom labels exist yet).
     * The query will return undefined if 404, and frontend should use default labels.
     */
    getUILabels: builder.query<CompanyUILabels | undefined, void>({
      // Using recommended route: /api/ui-labels (company from auth context)
      // This route does not require company_id in URL and is automatically resolved from auth
      queryFn: async (arg, api, extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: "api/ui-labels",
          method: "GET",
        });

        // Handle 404 as normal case (no custom labels exist yet)
        if (result.error && (result.error as any).status === 404) {
          // Return undefined to indicate no custom labels exist
          return { data: undefined };
        }

        // For other errors, return as-is
        if (result.error) {
          return result;
        }

        // Success case
        return { data: result.data as CompanyUILabels };
      },
      providesTags: [{ type: TAG_TYPE_CARD_PREFERENCES, id: "UI_LABELS" }],
    }),
    /**
     * PUT /api/ui-labels
     *
     * Updates or merges custom UI labels for the authenticated company.
     *
     * Response codes:
     * - 200: Success (returns updated CompanyUILabels)
     * - 404: Not found (use POST to create instead)
     * - 422: Validation error (check errors object)
     * - 403: Forbidden (user has no company associated)
     * - 401: Unauthorized (invalid/missing token)
     */
    updateUILabels: builder.mutation<CompanyUILabels, UpdateUILabelsRequest>({
      query: (data) => ({
        url: "api/ui-labels",
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [{ type: TAG_TYPE_CARD_PREFERENCES, id: "UI_LABELS" }],
    }),
    /**
     * POST /api/ui-labels
     *
     * Creates new custom UI labels for the authenticated company.
     *
     * Response codes:
     * - 201: Created (returns CompanyUILabels)
     * - 409: Conflict (labels already exist - use PUT instead)
     * - 422: Validation error (check errors object)
     * - 403: Forbidden (user has no company associated)
     * - 401: Unauthorized (invalid/missing token)
     */
    createUILabels: builder.mutation<CompanyUILabels, UpdateUILabelsRequest>({
      query: (data) => ({
        url: "api/ui-labels",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: TAG_TYPE_CARD_PREFERENCES, id: "UI_LABELS" }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUILabelsQuery,
  useUpdateUILabelsMutation,
  useCreateUILabelsMutation,
} = uiLabelApi;
